import appConfig from 'app-config';
import Actions from 'flow/actions';
import { Dispatch } from 'redux';

/**
 * WebSocket client for the in-meeting side-channel (video-chat service).
 *
 * Module-level WS reference rather than a Redux-stored handle — non-serializable
 * runtime objects don't belong in the store. The Redux slice
 * ({@code flow/reducers/video-chat-reducer.ts}) only tracks user-visible state
 * (messages, connected, error).
 *
 * Wire format (mirrors {@code WebSocketHandler.java} on the server):
 *   C→S message  {type:"message", text:string, isExternal:bool}
 *   S→C message  {type:"message", id:string, name:string, text:string, ts:number, isExternal:bool}
 *   S→C error    {type:"error",   reason:string}
 *
 * No history, no presence — the service is a fire-and-forget relay. Senders set
 * {@code isExternal} on each outgoing message (true for guests, false for
 * internal users who are also dual-writing to chat-service).
 */

let webSocket: WebSocket | null = null;

// Params of the current room, retained so the self-healing reconnect can
// re-open the socket without the caller re-supplying them. Cleared on an
// intentional close (leaving the meeting).
let reconnectParams: {
    roomId: string;
    name: string;
    baseUrl?: string;
} | null = null;

// Pending backoff timer for the next reconnect attempt, and the attempt
// counter that drives the exponential delay. Reset to 0 on every successful
// open.
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let reconnectAttempt = 0;

// Set while closeVideoChatWebSocket() is tearing the socket down on purpose,
// so the runtime onclose handler doesn't mistake it for a drop and schedule a
// reconnect. Also short-circuits a scheduled reconnect that hasn't fired yet.
let intentionalClose = false;

const BASE_RECONNECT_DELAY_MS = 1000;
const MAX_RECONNECT_DELAY_MS = 30000;

// Uniform float in [0, 1) from the Web Crypto RNG. Only used to jitter the
// reconnect backoff — not security-sensitive — but sourcing it here instead of
// Math.random avoids a static-analysis PRNG flag and is available in every
// browser/Electron runtime this code runs in.
const randomUnitInterval = (): number =>
    crypto.getRandomValues(new Uint32Array(1))[0] / 2 ** 32;

interface IncomingFrame {
    type: 'message' | 'error';
    id?: string;
    name?: string;
    text?: string;
    ts?: number;
    isExternal?: boolean;
    reason?: string;
}

const getWebSocketUrl = (
    roomId: string,
    name: string,
    baseUrl?: string,
): string | null => {
    // Local dev wins: when the localVideoChat flag is set we always force
    // localhost, so a regional prod URL baked into VideoConferenceConfig can't
    // hijack a local session.
    if (appConfig.localVideoChat) {
        return `ws://localhost:8443/room/${encodeURIComponent(roomId)}?name=${encodeURIComponent(name)}`;
    }
    // Production: baseUrl comes from VideoConferenceConfig.videoChatUrl,
    // populated per-region by the meeting-config provisioning. Returns null
    // when neither the local-dev flag nor the prod URL is configured —
    // caller logs and continues without the side-channel.
    if (baseUrl) {
        return `${baseUrl.replace(/\/$/, '')}/room/${encodeURIComponent(roomId)}?name=${encodeURIComponent(name)}`;
    }
    return null;
};

const handleFrame = (dispatch: Dispatch, data: string) => {
    let frame: IncomingFrame;
    try {
        frame = JSON.parse(data);
    } catch {
        // Don't log `data` — a malformed frame may still contain message text.
        console.warn('VideoChat: dropped invalid JSON frame');
        return;
    }

    switch (frame.type) {
        case 'message':
            dispatch({
                type: Actions.VIDEO_CHAT_MESSAGE_RECEIVED,
                message: {
                    id: frame.id,
                    name: frame.name,
                    text: frame.text,
                    // Default a missing/invalid ts so downstream
                    // synthesizeExternalMessage can't throw on new Date(ts).
                    ts: typeof frame.ts === 'number' ? frame.ts : Date.now(),
                    isExternal: frame.isExternal === true,
                },
            });
            break;
        case 'error':
            console.warn('VideoChat server error:', frame.reason);
            dispatch({
                type: Actions.VIDEO_CHAT_ERROR_RECEIVED,
                reason: frame.reason ?? 'error',
            });
            break;
        default:
            console.warn('VideoChat: unknown frame type', frame?.type);
    }
};

const closeExistingSocket = () => {
    if (!webSocket) {
        return;
    }
    try {
        webSocket.close();
    } catch {
        /* best-effort */
    }
    webSocket = null;
};

const clearReconnectTimer = () => {
    if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
    }
};

/**
 * Schedule a reconnect attempt with capped exponential backoff plus jitter.
 * No-op if a teardown has cleared `reconnectParams` (left the meeting) or a
 * timer is already pending. The relay is the guest's only chat channel, so we
 * keep retrying indefinitely rather than giving up after N attempts.
 */
const scheduleReconnect = (dispatch: Dispatch) => {
    if (intentionalClose || !reconnectParams || reconnectTimer) {
        return;
    }
    const delay = Math.min(
        BASE_RECONNECT_DELAY_MS * 2 ** reconnectAttempt,
        MAX_RECONNECT_DELAY_MS,
    );
    // Jitter (±25%) so a relay pod restart doesn't make every guest reconnect
    // in lock-step.
    const jittered = delay * (0.75 + randomUnitInterval() * 0.5);
    reconnectAttempt += 1;
    reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        if (intentionalClose || !reconnectParams) {
            return;
        }
        openSocket(dispatch, false);
    }, jittered);
};

const attachRuntimeHandlers = (ws: WebSocket, dispatch: Dispatch) => {
    ws.onmessage = (event: MessageEvent) => handleFrame(dispatch, event.data);
    ws.onclose = (event: CloseEvent) => {
        console.warn('VideoChat: closed', event.code, event.reason);
        // Only act if this is still the active socket. During a reconnect the
        // old socket's deferred onclose can fire after a new socket is already
        // in place — without this guard it would null the live ref and tear
        // down a healthy connection.
        if (webSocket !== ws) {
            return;
        }
        webSocket = null;
        // Intentional teardown (leaving the meeting) is handled by
        // closeVideoChatWebSocket, which already dispatched DISCONNECTED and
        // cleared the reconnect state — nothing to do here.
        if (intentionalClose) {
            return;
        }
        // Unexpected drop: flip to reconnecting (messages preserved) and start
        // the backoff loop. Recovers from relay-only drops that the meeting
        // monitor — which only watches the video-signaling WS/RTC — never sees.
        dispatch({ type: Actions.VIDEO_CHAT_RECONNECTING });
        scheduleReconnect(dispatch);
    };
    ws.onerror = (err: Event) => {
        console.error('VideoChat: error', err);
    };
};

/**
 * Open a socket using the retained `reconnectParams`. `isInitial` distinguishes
 * the first, caller-awaited attempt (which rejects on setup failure so
 * startMeeting can log it) from a background reconnect attempt (which never
 * rejects — a setup failure just schedules the next attempt).
 */
const openSocket = (dispatch: Dispatch, isInitial: boolean): Promise<void> =>
    new Promise((resolve, reject) => {
        const params = reconnectParams;
        const url = params
            ? getWebSocketUrl(params.roomId, params.name, params.baseUrl)
            : null;
        if (!url) {
            reject(new Error('VideoChat URL not configured'));
            return;
        }

        closeExistingSocket();

        const ws = new WebSocket(url);

        ws.onopen = () => {
            // A close request (leaving the meeting) can land while this socket
            // is mid-handshake. Because `webSocket` is only assigned here,
            // closeVideoChatWebSocket saw `null` and couldn't close this
            // in-flight socket — so honor the intent now: discard it instead of
            // adopting it, or we'd resurrect a live connection (and "connected"
            // UI state) for a meeting the user already left. Detach the
            // setup-phase handlers first so the close doesn't re-enter onclose.
            if (intentionalClose) {
                ws.onclose = null;
                ws.onerror = null;
                try {
                    ws.close(1000, 'leaving meeting');
                } catch {
                    /* best-effort */
                }
                resolve();
                return;
            }
            webSocket = ws;
            reconnectAttempt = 0;
            clearReconnectTimer();
            dispatch({ type: Actions.VIDEO_CHAT_CONNECTED });
            // Don't log `url` — it carries the participant's name in the
            // query string. Connection success is tracked via the
            // VIDEO_CHAT_CONNECTED action.
            attachRuntimeHandlers(ws, dispatch);
            resolve();
        };

        // Setup-phase handlers — replaced by attachRuntimeHandlers on
        // successful open.
        ws.onclose = (event: CloseEvent) => {
            console.warn(
                'VideoChat: closed during setup',
                event.code,
                event.reason,
            );
            if (isInitial) {
                reject(
                    new Error(`VideoChat closed during setup: ${event.code}`),
                );
                return;
            }
            // A reconnect attempt failed at setup — queue the next one and
            // resolve, since no caller is awaiting this promise.
            scheduleReconnect(dispatch);
            resolve();
        };
        ws.onerror = (err: Event) => {
            console.error('VideoChat: error during setup', err);
            if (isInitial) {
                reject(new Error('VideoChat error during setup'));
            }
            // On a reconnect attempt the paired onclose drives the retry.
        };
    });

/**
 * Open a WebSocket to the video-chat service for the given room. Idempotent —
 * if a socket is already open for any room it's closed first. The connection is
 * self-healing: an unexpected close triggers backoff reconnects until
 * closeVideoChatWebSocket is called.
 */
export const createVideoChatWebSocket =
    (roomId: string, name: string, baseUrl?: string) =>
    (dispatch: Dispatch): Promise<void> => {
        intentionalClose = false;
        reconnectAttempt = 0;
        clearReconnectTimer();
        reconnectParams = { roomId, name, baseUrl };
        return openSocket(dispatch, true);
    };

/** Close the WebSocket if open and stop any reconnect loop. Idempotent. */
export const closeVideoChatWebSocket =
    () =>
    (dispatch: Dispatch): void => {
        intentionalClose = true;
        reconnectParams = null;
        clearReconnectTimer();
        reconnectAttempt = 0;
        if (webSocket) {
            try {
                webSocket.close(1000, 'leaving meeting');
            } catch (e) {
                console.warn('VideoChat: close threw', e);
            }
            webSocket = null;
        }
        // Always reset the slice, even with no live socket: a close can arrive
        // while a reconnect is mid-handshake (webSocket still null), and without
        // this the panel would stay stuck in the `reconnecting` state. The
        // onopen guard above discards that in-flight socket when it resolves.
        dispatch({ type: Actions.VIDEO_CHAT_DISCONNECTED });
    };

/** Send a chat message. Server stamps id, name, ts. `isExternal` tags the
 *  origin so receivers can decide whether to render or drop. */
export const sendVideoChatMessage =
    (text: string, isExternal: boolean) =>
    (_dispatch: Dispatch): void => {
        if (!webSocket || webSocket.readyState !== WebSocket.OPEN) {
            console.warn('VideoChat: send dropped — socket not open');
            return;
        }
        webSocket.send(JSON.stringify({ type: 'message', text, isExternal }));
    };
